import { useMutation } from '@apollo/client';
import type {
  CreateThirdPartyMutation,
  InsertThirdPartyInput,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { CreateThirdPartyDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import {
  toCriticality,
  toThirdPartyStatus,
  toThirdPartyType,
} from './thirdPartyEnums';
import { useInsertThirdPartyTRPC } from './useInsertThirdPartyTRPC';

/**
 * Public input for the wrapper. Matches the third-party form call site where
 * Type/Status are plain strings (the select options ARE the enum values) and
 * Criticality is a coerced number. Everything else mirrors the GraphQL
 * InsertThirdPartyInput (required id arrays). The wrapper narrows
 * Type/Status/Criticality to the strict tRPC input shape via runtime parse
 * (no casts).
 */
type InsertThirdPartyInputShape = InsertThirdPartyInput;

export const useInsertThirdParty = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [insertThirdPartyGraphQL, graphqlState] = useMutation(
    CreateThirdPartyDocument,
    {
      update: (cache) => {
        evictField(cache, 'third_party');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useInsertThirdPartyTRPC();

  const insertThirdParty = async (
    variables: InsertThirdPartyInputShape
  ): Promise<CreateThirdPartyMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertThirdParty({
        ...variables,
        Type: toThirdPartyType(variables.Type),
        Status: toThirdPartyStatus(variables.Status),
        Criticality: toCriticality(variables.Criticality),
      });
    }

    const result = await insertThirdPartyGraphQL({
      variables: { object: variables },
    });
    if (!result.data) {
      throw new Error('Failed to insert third party');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      insertThirdParty,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertThirdParty,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
